import React, { lazy, Suspense, useMemo } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { useAuth } from "../Provider/AuthProvider";
import { useAppSelector } from "../store/hooks";
import {
  getPermissionsFromEntitlement,
  CHANGE_REQUESTS,
  CHANGE_REQUEST_QUEUE_MANAGER_LIST,
  CREATE_CHANGE_REQUEST,
  CHANGE_REQUEST_PENDING_APPROVALS,
  CHANGE_REQUESTS_CREATED_BY_ME,
  CHANGE_REQUESTS_ASSIGNED_TO_ME,
  CHANGE_REQUEST_MY_TASKS,
  MY_ASSIGNED_TICKETS,
} from "../constants/permissionKeys";
import {
  PERMISSION_TO_ROUTE,
  PERMISSION_TO_COMPONENT,
} from "../utils/permissionMappings";

import Loader from "../Loader";
import PendingApprovalsHub from "../components/ApproveTicket/PendingApprovalsHub";
import MyAssignedHub from "../components/TicketManagement/MyAssignedHub";
import { NotificationProvider } from "../components/notifications/NotificationProvider";
import TermsOfUse from "../components/Terms/TermsOfUse";

const LoginPage = lazy(() => import("../components/login/LoginPage"));
const TicketDetail = lazy(
  () => import("../components/TicketManagement/TicketDetail"),
);
const Dashboard = lazy(() => import("../components/Dashboard/Dashboard"));
const Logout = lazy(() => import("../utils/Logout"));
const DashboardRedirect = lazy(() => import("../routes/DashboardRedirect"));
const Unauthorized = lazy(() => import("../components/common/Unauthorized"));
const ForgotPasswordOtp = lazy(() => import("../components/auth/ForgotPasswordOtp"));
const ForgotPasswordReset = lazy(() => import("../components/auth/ForgotPasswordReset"));
const BannerFormPage = lazy(() => import("../components/Banner/BannerFormPage"));
const SupportTeamTicketsHub = lazy(
  () => import("../components/TicketManagement/SupportTeamTicketsHub"),
);

const routesForPublic = [
  { path: "/", element: <LoginPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/auth/forgot-password/otp", element: <ForgotPasswordOtp /> },
  { path: "/auth/forgot-password/reset", element: <ForgotPasswordReset /> },
  { path: "/logout", element: <Logout /> },
  { path: "/terms-of-use", element: <TermsOfUse /> },
  { path: "/unauthorized", element: <Unauthorized /> },
];

const Routes = () => {
  const { token, isAuthenticated, role } = useAuth();
  const entitlement = useAppSelector((state) => state.userEntitlement?.data);
  const entitlementLoading = useAppSelector(
    (state) => state.userEntitlement?.loading,
  );
  const authInitializing = Boolean(token) && !role;
  const userId = entitlement?.id ?? entitlement?.userId ?? null;

  // Get dashboard path based on role
  const getDashboardPath = (userRole) => {
    const roleToPath = {
      USER: "/user-dashboard",
      APPROVER: "/approver-dashboard",
      ADMIN: "/admin-dashboard",
      SUPER_ADMIN: "/superadmin-dashboard",
      SUPPORT_ADMIN: "/supportadmin-dashboard",
      CONSULTANT: "/consultant-dashboard",
      QUEUE_MANAGER: "/queuemanager-dashboard",
    };
    return roleToPath[userRole] || "/user-dashboard";
  };

  // Build routes dynamically from entitlement
  const buildDashboardRoutes = useMemo(() => {
    if (!entitlement || !role) {
      return [];
    }

    const permissions = getPermissionsFromEntitlement(entitlement);
    const permissionSet = new Set(permissions);
    const routes = [];
    const routePaths = new Set();
    const ChangeRequestListComponent = PERMISSION_TO_COMPONENT[CHANGE_REQUESTS];

    // Build routes from permissions
    permissions.forEach((permissionKey) => {
      const routePath = PERMISSION_TO_ROUTE[permissionKey];
      const Component = PERMISSION_TO_COMPONENT[permissionKey];

      // Built once below from individual CR / my-assigned permissions.
      if (routePath === "change-requests" || routePath === "my-assigned-tickets") {
        return;
      }

      if (routePath && Component && !routePaths.has(routePath)) {
        // Handle routes with ticket detail children
        if (
          routePath === "all-tickets" ||
          routePath === "approvetickets" ||
          routePath === "my-tickets" ||
          routePath === "my-previous-tickets" ||
          routePath === "my-previous-change-requests" ||
          routePath === "awaiting-closure" ||
          routePath === "closed-tickets"
        ) {
          if (routePath === "my-previous-change-requests") {
            routes.push({
              path: routePath,
              children: [
                { path: "", element: <Component /> },
                {
                  path: "edit/:id",
                  element: ChangeRequestListComponent ? (
                    <ChangeRequestListComponent mode="edit" />
                  ) : (
                    <Component mode="edit" />
                  ),
                },
              ],
            });
          } else {
            routes.push({
              path: routePath,
              children: [
                { path: "", element: <Component /> },
                { path: ":ticketId", element: <TicketDetail /> },
              ],
            });
          }
          // Alias route for the sidebar "Support Team" menu.
          // Uses same AllTickets component; filters are applied via query params.
          if (routePath === "all-tickets") {
            routes.push({
              path: "support-team-tickets",
              element: <SupportTeamTicketsHub />,
            });
          }
        } else if (routePath === "company-register") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: ":id", element: <Component /> },
            ],
          });
        } else if (routePath === "user-registration") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: "view/:id", element: <Component /> },
              // keep edit route compatible when navigated as child
              { path: "edit/:id", element: <Component /> },
            ],
          });
        } else if (routePath === "baner-management") {
          routes.push({
            path: routePath,
            children: [
              { path: "", element: <Component /> },
              { path: "create", element: <BannerFormPage /> },
              { path: "edit/:id", element: <BannerFormPage /> },
            ],
          });
        } else {
          // Simple routes (including nested routes)
          routes.push({
            path: routePath,
            element: <Component />,
          });
        }
        routePaths.add(routePath);
      }
    });

    if (!routePaths.has("change-requests")) {
      const hasList = permissionSet.has(CHANGE_REQUESTS);
      const hasQmList = permissionSet.has(CHANGE_REQUEST_QUEUE_MANAGER_LIST);
      const hasCreate = permissionSet.has(CREATE_CHANGE_REQUEST);
      const hasPending = permissionSet.has(CHANGE_REQUEST_PENDING_APPROVALS);
      const hasCreatedByMe = permissionSet.has(CHANGE_REQUESTS_CREATED_BY_ME);
      const hasAssignedToMe = permissionSet.has(CHANGE_REQUESTS_ASSIGNED_TO_ME);
      const companyObj = entitlement?.company || entitlement?.user?.company || {};
      const companyName = String(companyObj?.name || companyObj?.companyName || "").toLowerCase();
      const isClientCompany = companyObj?.isClient === true;
      const isRootCompany =
        companyObj?.isRootCompany === true ||
        companyObj?.isRoot === true ||
        !isClientCompany ||
        companyName.includes("smac");
      const allowedRootRoles = new Set(["ADMIN", "QUEUE_MANAGER", "CONSULTANT"]);
      const isAllowedRootRole = allowedRootRoles.has(String(role || "").toUpperCase());

      const hasMyTasks =
        (permissionSet.has(CHANGE_REQUEST_MY_TASKS) || (isRootCompany && isAllowedRootRole)) &&
        isAllowedRootRole;

      if (
        hasList ||
        hasQmList ||
        hasCreate ||
        hasPending ||
        hasCreatedByMe ||
        hasAssignedToMe ||
        hasMyTasks
      ) {
        const changeRequestChildren = [];
        const MyTasksComponent = PERMISSION_TO_COMPONENT[CHANGE_REQUEST_MY_TASKS];

        if ((hasList || hasQmList) && ChangeRequestListComponent) {
          changeRequestChildren.push({
            path: "",
            element: <ChangeRequestListComponent />,
          });
        }
        if (hasCreatedByMe && ChangeRequestListComponent) {
          changeRequestChildren.push({
            path: "created-by-me",
            element: <ChangeRequestListComponent createdByMeOnly />,
          });
        }
        if (hasMyTasks && MyTasksComponent) {
          changeRequestChildren.push({
            path: "my-tasks",
            element: <MyTasksComponent />,
          });
        }
        if (hasCreate && ChangeRequestListComponent) {
          changeRequestChildren.push({
            path: "create",
            element: <ChangeRequestListComponent mode="create" />,
          });
        }
        if (hasPending) {
          changeRequestChildren.push({
            path: "pending-approvals",
            element: (
              <PendingApprovalsHub initialTab="change-requests" />
            ),
          });
        }
        // Edit must exist for every CR permission set (created-by-me, assigned-to-me, etc.)
        if (ChangeRequestListComponent) {
          const childPaths = new Set(changeRequestChildren.map((c) => c.path));
          if (!childPaths.has("edit/:id")) {
            changeRequestChildren.push({
              path: "edit/:id",
              element: <ChangeRequestListComponent mode="edit" />,
            });
          }
        }
        if (changeRequestChildren.length > 0) {
          routes.push({
            path: "change-requests",
            children: changeRequestChildren,
          });
          routePaths.add("change-requests");
        }
      }
    }

    if (!routePaths.has("my-assigned-tickets")) {
      const hasAssignedTickets = permissionSet.has(MY_ASSIGNED_TICKETS);
      const hasCrAssigned = permissionSet.has(CHANGE_REQUESTS_ASSIGNED_TO_ME);
      if (hasAssignedTickets || hasCrAssigned) {
        const myAssignedChildren = [
          { path: "", element: <MyAssignedHub /> },
        ];
        if (hasAssignedTickets) {
          myAssignedChildren.push({
            path: ":ticketId",
            element: <TicketDetail />,
          });
        }
        routes.push({
          path: "my-assigned-tickets",
          children: myAssignedChildren,
        });
        routePaths.add("my-assigned-tickets");
      }
    }

    return routes;
  }, [entitlement, role]);

  // Build dashboard routes dynamically
  const routesForAuthenticatedOnly = useMemo(() => {
    if (!role) return [];

    const dashboardPath = getDashboardPath(role);
    const dashboardChildren = entitlementLoading
      ? [{ path: "*", element: <Loader /> }]
      : buildDashboardRoutes.length > 0
        ? [
            { path: "", element: <DashboardRedirect /> },
            ...buildDashboardRoutes,
            // Support team shortcut (from sidebar dynamic menu).
            // This must exist even if the user doesn't have an explicit ALL_TICKETS menu/route entry.
            { path: "support-team-tickets", element: <SupportTeamTicketsHub /> },
            { path: "*", element: <Navigate to="/unauthorized" replace /> },
          ]
        : [{ path: "*", element: <Navigate to="/unauthorized" replace /> }];

    return [
      {
        path: "/dashboard",
        element: <Navigate to={dashboardPath} replace />,
      },
      {
        path: dashboardPath,
        element: <Dashboard />,
        children: dashboardChildren,
      },
    ];
  }, [role, buildDashboardRoutes, entitlementLoading]);

  const router = useMemo(() => {
    return createBrowserRouter([
      ...routesForPublic,
      ...routesForAuthenticatedOnly,
      {
        path: "*",
        element: authInitializing ? (
          <Loader />
        ) : isAuthenticated ? (
          <Navigate to="/unauthorized" replace />
        ) : (
          <Navigate to="/" replace />
        ),
      },
    ]);
  }, [routesForAuthenticatedOnly, authInitializing, isAuthenticated]);

  if (authInitializing) {
    return (
      <NotificationProvider userId={userId}>
        <Loader />
      </NotificationProvider>
    );
  }

  return (
    <NotificationProvider userId={userId}>
      <Suspense fallback={<Loader />}>
        <RouterProvider router={router} />
      </Suspense>
    </NotificationProvider>
  );
};

export default Routes;
